import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { fetchMessages } from "../smsApi";
import { formatPhone } from "../phone";
import type { SmsMessage } from "../types";

const PAGE_SIZE = 50;

type DirectionFilter = "all" | "inbound" | "outbound";

function formatDate(dateStr: string): string {
  try {
    return format(new Date(dateStr), "MMM d, yyyy h:mm a");
  } catch {
    return dateStr;
  }
}

function messagesToCsv(messages: SmsMessage[]): string {
  const headers = [
    "Direction",
    "From",
    "To",
    "Body",
    "Phone Line",
    "Time",
    "Status",
  ];
  const rows = messages.map((m) => [
    m.direction,
    m.from_number,
    m.to_number,
    `"${(m.body || "").replace(/"/g, '""')}"`,
    m.phone_line,
    m.created_at,
    m.status ?? "",
  ]);
  return [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
}

function downloadCsv(csv: string, filename: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

type Props = {
  phoneLine: "line1" | "line2" | null;
};

export function MessageLogView({ phoneLine }: Props) {
  const [messages, setMessages] = useState<SmsMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [directionFilter, setDirectionFilter] = useState<DirectionFilter>("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(0);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchMessages({ phoneLine, limit: 5000, includeHidden: true });
      setMessages(rows);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [phoneLine]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    setPage(0);
  }, [directionFilter, dateFrom, dateTo, phoneLine]);

  const filtered = messages.filter((m) => {
    if (directionFilter !== "all" && m.direction !== directionFilter) return false;
    if (dateFrom && new Date(m.created_at) < new Date(dateFrom)) return false;
    if (dateTo && new Date(m.created_at) > new Date(dateTo + "T23:59:59")) {
      return false;
    }
    return true;
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  function handleExport() {
    const csv = messagesToCsv(filtered);
    const stamp = format(new Date(), "yyyy-MM-dd_HHmm");
    downloadCsv(csv, `tribal-axe-sms-log-${stamp}.csv`);
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-shrink-0 flex-wrap items-center gap-3 border-b border-tribal-border bg-tribal-surface px-4 py-3">
        <select
          value={directionFilter}
          onChange={(e) => setDirectionFilter(e.target.value as DirectionFilter)}
          className="rounded-lg border border-tribal-border bg-tribal-bg px-3 py-1.5 text-xs text-gray-300 focus:border-tribal-red focus:outline-none"
        >
          <option value="all">All Directions</option>
          <option value="inbound">Inbound</option>
          <option value="outbound">Outbound</option>
        </select>

        <div className="flex items-center gap-2 text-xs text-tribal-muted">
          <span>From</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="rounded-lg border border-tribal-border bg-tribal-bg px-2 py-1.5 text-xs text-gray-300 focus:border-tribal-red focus:outline-none"
          />
          <span>to</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="rounded-lg border border-tribal-border bg-tribal-bg px-2 py-1.5 text-xs text-gray-300 focus:border-tribal-red focus:outline-none"
          />
        </div>

        <div className="flex-1" />

        <span className="text-xs text-tribal-muted">
          {filtered.length} message{filtered.length !== 1 ? "s" : ""}
        </span>

        <button
          type="button"
          onClick={handleExport}
          disabled={filtered.length === 0}
          className="rounded-lg bg-tribal-border px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-white/10 disabled:opacity-40"
        >
          Export CSV
        </button>
      </div>

      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex h-full items-center justify-center text-sm text-tribal-muted">
            <div className="animate-pulse">Loading messages…</div>
          </div>
        ) : error ? (
          <div className="m-4 rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">
            <p className="font-medium">Failed to load messages</p>
            <p className="mt-1 font-mono text-xs">{error}</p>
          </div>
        ) : paged.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-tribal-muted">
            No messages found
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-tribal-surface text-xs uppercase tracking-wider text-tribal-muted">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Direction</th>
                <th className="px-4 py-3 text-left font-medium">From</th>
                <th className="px-4 py-3 text-left font-medium">To</th>
                <th className="px-4 py-3 text-left font-medium">Message</th>
                <th className="px-4 py-3 text-left font-medium">Line</th>
                <th className="px-4 py-3 text-left font-medium">Time</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-tribal-border/60">
              {paged.map((msg) => (
                <tr key={msg.id} className="transition-colors hover:bg-white/5">
                  <td className="px-4 py-2.5">
                    <span
                      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${
                        msg.direction === "inbound"
                          ? "bg-blue-900/30 text-blue-400"
                          : "bg-green-900/30 text-green-400"
                      }`}
                    >
                      {msg.direction === "inbound" ? "↓ In" : "↑ Out"}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-gray-300">
                    {formatPhone(msg.from_number)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-gray-300">
                    {formatPhone(msg.to_number)}
                  </td>
                  <td className="max-w-xs truncate px-4 py-2.5 text-gray-300">
                    {msg.body}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-tribal-muted">
                    {msg.phone_line === "line1"
                      ? "L1"
                      : msg.phone_line === "line2"
                      ? "L2"
                      : msg.phone_line}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-xs text-tribal-muted">
                    {formatDate(msg.created_at)}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={`text-xs ${
                        msg.status === "sent" || msg.status === "delivered"
                          ? "text-green-400"
                          : msg.status === "failed"
                          ? "text-red-400"
                          : "text-tribal-muted"
                      }`}
                    >
                      {msg.status ?? "—"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex flex-shrink-0 items-center justify-between border-t border-tribal-border bg-tribal-surface px-4 py-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="text-xs text-tribal-muted transition-colors hover:text-white disabled:opacity-30"
          >
            ← Previous
          </button>
          <span className="text-xs text-tribal-muted">
            Page {page + 1} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="text-xs text-tribal-muted transition-colors hover:text-white disabled:opacity-30"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
